import React, { useRef } from 'react';
import {
  type InsuranceCardExtractionResponse,
  useUploadInsuranceCardMutation,
} from '@features/patient-coverages/store/api';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { Button, CircularProgress } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface InsuranceCardUploadProps {
  patientId: string;
  onExtracted: (data: InsuranceCardExtractionResponse) => void;
  disabled?: boolean;
}

const InsuranceCardUpload: React.FC<InsuranceCardUploadProps> = ({
  patientId,
  onExtracted,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadInsuranceCard, { isLoading }] = useUploadInsuranceCardMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { insuranceCard } = useEventTracking();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    // Validate files
    const files = Array.from(selectedFiles).slice(0, 2);
    for (const file of files) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        enqueueSnackbar('Please select a JPEG, PNG, or WebP image.', { variant: 'error' });
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        enqueueSnackbar(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit.`, {
          variant: 'error',
        });
        return;
      }
    }

    insuranceCard.uploadStarted({ file_count: files.length });

    try {
      const result = await uploadInsuranceCard({ patientId, files }).unwrap();
      insuranceCard.uploadCompleted({
        confidence: result.confidence,
        has_front: !!result.card_image_url_front,
        has_back: !!result.card_image_url_back,
      });
      onExtracted(result);

      // The BE now auto-creates the coverage in the same call (GDN-1032
      // follow-up). Three outcomes worth distinguishing in the toast:
      //  - coverage_id present + member_id present → fully active
      //  - coverage_id present + member_id null    → saved as pending,
      //    flag the missing field so the provider knows why
      //  - coverage_id null                        → BE refused to
      //    create (typically a primary already exists). The form is
      //    prefilled — provider can save manually.
      if (result.coverage_id) {
        enqueueSnackbar(
          result.member_id
            ? 'Insurance card scanned — coverage saved.'
            : 'Coverage saved as pending — add a member ID to activate.',
          { variant: 'success' },
        );
      } else {
        enqueueSnackbar(
          'Card scanned — review the prefilled fields and save the coverage.',
          { variant: 'info' },
        );
      }
    } catch {
      insuranceCard.uploadFailed({ error_type: 'api_error' });
      enqueueSnackbar('Failed to extract insurance card data. Please try again.', {
        variant: 'error',
      });
    }

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-label="Upload insurance card images"
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={isLoading ? <CircularProgress size={16} /> : <CameraAltIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || isLoading}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        {isLoading ? 'Extracting...' : 'Scan Card'}
      </Button>
    </>
  );
};

export default InsuranceCardUpload;
